import { useEffect, useState } from 'react';
import { AssociatedLink } from 'features/catalog/badge-associated-links.ts';
import {
  BADGE_DESCRIPTIONS,
  BadgeDescription,
  type BadgeStats,
  resolveRestrictedRules,
} from 'features/catalog/badge-definitions.ts';
import { isBadgeReleased } from 'features/catalog/badge-release.ts';
import {
  asRarityName,
  getRarityImageUrlByName,
  rarityPoints,
  RarityName,
  RarityPoints,
} from 'features/catalog/rarity.ts';

/** Final shape of each Badge.
 *  - "credits" or "design" can each exist, or just one of them, depending on your data.
 */
export interface Badge {
  id: string;
  name: string;
  description: string;
  credits?: string;
  design?: string;
  participantIDsWhoOwnThisBadge: string[];
  /**
   * Owners with a feed-visible update after the catalog drop — numerator for claim %
   * when that eligibility rate beats the Claimed-By / engaged-profiles floor.
   * May be lower than `participantIDsWhoOwnThisBadge.length` when stale owners
   * are listed in Claimed By but haven’t re-submitted yet, or when owners have
   * fewer than 5 badges (those profiles are omitted from claim %).
   */
  claimRateOwnerCount?: number;
  /**
   * Owners with 5+ badges — numerator for the Claimed-By / engaged-profiles floor.
   */
  claimFloorOwnerCount?: number;
  /**
   * Profiles with a Badges Feed claim/join submission after this badge’s catalog
   * drop (same noon stamp + earlier-same-day-claim nudge as the feed). Claim % =
   * claimRateOwnerCount / eligibleClaimProfileCount, floored at engaged owners /
   * engaged profiles. Profiles with fewer than 5 badges are omitted.
   */
  eligibleClaimProfileCount?: number;
  /** Engaged profiles (5+ badges) at ownership rebuild — used to star low-sample claim %. */
  claimUniverseProfileCount?: number;
  /**
   * Commonness score from eligibility-aware claim rate (owners / eligible),
   * projected onto the full profile universe — same basis as the `22%*` hover label.
   */
  badgeScore?: number;

  /** Rarity info */
  rarityName: RarityName;
  rarityScore: RarityPoints;

  /** Possible images */
  imageUrl: string;
  smallImageUrl: string;
  IDImageUrl: string;
  smallIDImageUrl: string;
  rarityImageUrl: string;

  /** Fields that getAutoUpdatedMap might need */
  autoClaimRules?: { ids: string[]; count: number };
  banSelfClaim?: boolean;
  /** Custom hover lock line for `banSelfClaim` (shown as `🔒 …`). */
  banSelfClaimHint?: string;
  /** When true, sorts near the end of its group. `banSelfClaim` sorts even later automatically. */
  forceSortLast?: boolean;
  /** When true, never surface this badge in "Claimed by others" suggestions. */
  suppressSuggest?: boolean;
  restrictedRules?: { ids: string[]; count: number };
  /** Thematic neighbors from the catalog (not unlock rules). */
  relatedIds?: string[];
  /** Character-sheet axes (0–5), copied from the catalog. */
  stats: BadgeStats;

  associatedLinks?: AssociatedLink[];
}

/** Filename used for a badge's public image assets. */
export function badgeImageFileName(name: string): string {
  return name.replace(/[:,\s]+/g, '_') + '.png';
}

/** Small thumbs used by the scrolling badge field — available before badgesMap loads. */
export function getBadgeBackdropImageUrls(): string[] {
  const urls: string[] = [];
  for (const [badgeId, desc] of BADGE_DESCRIPTIONS) {
    if (!isBadgeReleased(badgeId)) continue;
    urls.push(`${process.env.PUBLIC_URL}/images/resized-images/${badgeImageFileName(desc.name)}`);
  }
  return urls;
}

/**
 * Loads all badges from BADGE_DESCRIPTIONS into a Map<string, Badge>,
 * preserving credits/design fields, and any auto-claim/restricted rules.
 *
 * @param enabled When false, keeps an empty map so callers can sequence boot
 *   (images → field → badges → profiles).
 */
export function useBadges(enabled = true) {
  const [badgesMap, setBadgesMap] = useState<Map<string, Badge>>(new Map());

  useEffect(() => {
    if (!enabled) {
      setBadgesMap(new Map());
      return;
    }

    const map = new Map<string, Badge>();

    BADGE_DESCRIPTIONS.forEach((desc: BadgeDescription, badgeId: string) => {
      const badgeImageName = badgeImageFileName(desc.name);

      const rarityName = asRarityName(desc.rarity);

      map.set(badgeId, {
        id: badgeId,
        name: desc.name,
        description: desc.description,
        credits: desc.credits,
        design: desc.design,
        associatedLinks: desc.associatedLinks,

        rarityScore: rarityPoints[rarityName],
        rarityName,
        imageUrl: `${process.env.PUBLIC_URL}/images/${badgeImageName}`,
        smallImageUrl: `${process.env.PUBLIC_URL}/images/resized-images/${badgeImageName}`,
        IDImageUrl: `${process.env.PUBLIC_URL}/images/${badgeId}.png`,
        smallIDImageUrl: `${process.env.PUBLIC_URL}/images/resized-images/${badgeId}.png`,
        rarityImageUrl: getRarityImageUrlByName(rarityName),

        // Pass along any rules
        autoClaimRules: desc.autoClaimRules,
        banSelfClaim: desc.banSelfClaim,
        banSelfClaimHint: desc.banSelfClaimHint,
        forceSortLast: desc.forceSortLast,
        suppressSuggest: desc.suppressSuggest,
        restrictedRules: resolveRestrictedRules(desc),
        relatedIds: desc.relatedIds,
        stats: desc.stats,
        participantIDsWhoOwnThisBadge: [],
      });
    });

    setBadgesMap(map);
  }, [enabled]);

  return { badgesMap, setBadgesMap };
}
